import React, { useEffect, useState, useRef } from 'react';
import { connect, useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { array, bool, func, number, object, string, shape } from 'prop-types';
import { bindActionCreators } from 'redux';
import { showSlideout, hideSlideout } from 'ps-components/Slideout/SlideoutReducer';
import { dataRefetched } from '../MarketPricingActions';
import classnames from 'classnames';
import Dropdown from 'ps-components/Dropdown/Dropdown';
import DropdownViews from 'ps-components/Dropdown/DropdownViews';
import FacetPanel from 'ps-components/FacetPanel/FacetPanel';
import FacetPanelLoading from 'ps-components/FacetPanel/FacetPanelLoading/FacetPanelLoading';
import getHiddenFacets from 'ps-components/FacetPanel/getHiddenFacets';
import isIE from 'ps-components/Utils/checkForIE';
import LoadingTable from 'ps-components/AdvancedTable/LoadingTable/LoadingTable';
import Pagination from 'ps-components/Pagination/Pagination';
import Search from 'ps-components/Search/Search';
import { Button, Tooltip, Menu } from '@mantine/core';

import { areAllFacetsUnchecked, getCMSQueryParams } from '../../../lib/facetHelpers';
import { landingPageGridActions } from './LandingPageGrid/LandingPageGridCreate';
import { landingPageJobFacetSearchActions } from './LandingPageFacet';
import { PAGE, PAGE_CODE, CATEGORY_CODE } from '../../../lib/pageCodeDefaults';
import { setSelectedBenchmarkYear } from '../../CompensationSummary/CompensationSummaryActions';
import { getEmployeeSelectedView } from '../JobEmployees/JobEmployeesActions';
import { getJobDetailsSelectedView } from '../JobDetails/JobDetailsActions';
import { SLIDEOUTS as MP_SLIDEOUTS } from '../../Slideout/Slideouts';
import * as landingPageActions from './LandingPageActions';
import AdvancedSearch from '../AdvancedSearch/AdvancedSearch';
import FacetHeader from '../AdvancedSearch/FacetHeader/FacetHeader';
import LandingPageGrid from './LandingPageGrid/LandingPageGrid';
import Placeholder from '../Placeholder/Placeholder';
import SearchInfo from '../SearchInfo/SearchInfo';
import { getJobsByID } from '../../../components/JobMatching/OrgJobsActions';
import { jobMatchGridActions } from './JobMatchDetail/JobMatchGridCreate';

import './LandingPage.scss';

LandingPage.propTypes = {
  actions: object,
  benchmarkYear: number,
  dispatch: func,
  grid: object,
  group: object,
  history: object,
  jobFacetSearch: object,
  landingPageSelectedView: shape({
    selectedViewPk: number,
    loading: bool,
    loaded: bool,
    status: number,
    error: string
  }),
  landingPageJobDetails: object,
  landingPageViews: shape({
    views: array,
    loading: bool,
    loaded: bool,
    status: number,
    error: string
  }),
  setLandingPageSelectedView: shape({
    loading: bool
  }),
  location: object,
  masterAccountId: string,
  match: object,
  modelDetails: object,
  orgJobsSearchText: string,
  organizationCode: string,
  permissions: object,
  readOnlyUser: bool,
  replacementTokens: array,
  restrictedUser: bool,
  useJobCodeKey: bool,
  gridActions: object,
  subsite: string,
  marketPricing: object,
  fieldFormatting: shape({
    numericRounding: number,
    monetaryRounding: number,
    percentRounding: number
  }),
  setSelectedBenchmarkYear: func,
  userRights: object,
  groupAdminCanConfigureViews: bool,
  regularUserCanConfigureViews: bool,
  useJobCodeKey: bool,
  usePropagateMatches: bool
};

function LandingPage(props) {
  const facetId = 'landing-page__facet-panel';
  const advancedSearchFilterKey = facetId + '_advanced-search-filters';
  const [searchText, setSearchText] = useState('');
  const [firstPageLoaded, setFirstPageLoaded] = useState(false);
  const [facetCollapse, setFacetCollapse] = useState(true);
  const [useFacetSearch, setUseFacetSearch] = useState(!(localStorage.getItem(facetId + '_search_mode') === 'false'));
  const searchInputRef = useRef();

  const {
    facetSelection,
    facets,
    limit,
    loaded,
    loading,
    name: jobFacetName,
    offset,
    query,
    showZeros,
    lastFacetFilteredID,
    lastQueryParams,
    error
  } = props.jobFacetSearch;

  const { benchmarkYear, slideoutOpen, refetchGridData } = props;
  const { numericRounding, percentRounding } = props.fieldFormatting;
  const showMarketplaceMatchesSlideoutFeatureFlag = props.launchDarklyFeatureFlags['mpm-slideout'];
  const marketPricingDisplayIn = useSelector(state => state.marketPricing.MarketPricingReducer.displayIn);
  const jobMatchDisplayIn = useSelector(state => state.landingPage.jobMatchGridReducer.displayIn);
  const selectedJobs = useSelector(state => state.landingPage.landingPageReducer.selectedJobs);
  const nonSlottedJobs = selectedJobs.filter(job => job.pricingStatus !== 'slotted');
  const notPricedJobs = selectedJobs.filter(job => job.pricingStatus === null);

  const disableBulkPricing = nonSlottedJobs.length === 0 || nonSlottedJobs.length < selectedJobs.length || nonSlottedJobs.length > 50;
  const disablePropagateMatches = notPricedJobs.length > 0;

  const dispatch = useDispatch();

  useEffect(() => {
    if (firstPageLoaded && !loading) {
      props.gridActions.clearSort();
      if (useFacetSearch) {
        setSearchText(query);
        searchInputRef.current?.setSearchValue(query);
        props.actions.searchInputSearch(query);
      }
    }
  }, [query, props.grid?.data?.length ? props.location.search : '', props.landingPageSelectedView?.selectedViewPk]);

  useEffect(() => {
    setFirstPageLoaded(true);
    // hide any slideouts when loading page for first time
    props.actions.hideSlideout();

    if (!loading && props.location?.state?.notFirstLoad && useFacetSearch) {
      const { locationOffset, searchQuery } = props.location.state;
      setSearchText(searchQuery || query);
      searchInputRef.current?.setSearchValue(searchQuery || query);
      props.actions.searchInputSearch(searchQuery || query, { offset: locationOffset || offset });
    }

    if (!loading && !props.location?.state?.notFirstLoad && useFacetSearch) {
      setSearchText('');
      searchInputRef.current?.setSearchValue('');

      props.actions.search({
        params: {
          ...getCMSQueryParams(jobFacetName),
          excludeJobsWithZeroEmployees: props.excludeJobsWithZeroEmployees,
          firstPageLoad: true
        },
        clear: true
      });
    }

    // records per page isn't sticky and resets to 50 on page reload
    pageOrgJobs(50, offset);

    if (!props.history.location.state?.bulkPricing) {
      clearAllSelections();
      window.localStorage.removeItem('selectedJobs');
    }

    return () => {
      props.gridActions.clearSort();
      if (!props.history.location.state?.bulkPricing) {
        clearAllSelections();
        window.localStorage.removeItem('selectedJobs');
      }
      else props.history.replace({ ...props.history.location, state: { bulkPricing: false } });
    };
  }, []);

  useEffect(() => {
    if (!props.landingPageViews?.loading) {
      refreshViews();
    }
  }, [props.location.search]);

  useEffect(() => {
    if (!slideoutOpen && refetchGridData) {
      props.actions.search({
        params: {
          ...getCMSQueryParams(jobFacetName),
          excludeJobsWithZeroEmployees: props.excludeJobsWithZeroEmployees,
          offset,
          limit,
          firstPageLoad: false
        },
        clear: false
      });
      props.actions.dataRefetched();
    }
  }, [slideoutOpen, refetchGridData]);

  // resets job match tables when the pricing display setting is changed
  useEffect(() => {
    if (jobMatchDisplayIn !== marketPricingDisplayIn) {
      dispatch(jobMatchGridActions.setIsStale('all', true));
      dispatch(jobMatchGridActions.updateDisplayIn(marketPricingDisplayIn));
    } else return;
  }, [marketPricingDisplayIn]);

  function refreshViews() {
    props.actions.getLandingPageViews(props.masterAccountId).then(() => {
      props.actions.getLandingPageSelectedView(props.masterAccountId);
    });
  }

  // scrollRef used to scroll up to top after search or pagination change
  let scrollRef = React.createRef();

  // Determine job code key text replacement text
  const hasJobCodeKeyReplacement = props.replacementTokens?.find(value => value.token === 'job_code_key_text');
  const jobCodeKeyText = hasJobCodeKeyReplacement?.value || 'Job Code Key';

  // Determine whether views, selected view, or grid data are loading
  const isLoading =
    (props.landingPageViews?.loading ||
      props.landingPageSelectedView?.loading ||
      props.setLandingPageSelectedView?.loading ||
      props.grid.processInitialLoad) &&
    !error;

  // Show 'Reset All' button in facet panel header if any options are unselected in facet panel
  const showResetAllButton =
    useFacetSearch && (!areAllFacetsUnchecked(facets, facetSelection) || (searchText && searchText !== ''));

  // Logic for Reset All button
  function handleReset() {
    setSearchText('');
    searchInputRef.current?.setSearchValue('');
    props.actions.search({
      params: { ...getCMSQueryParams(jobFacetName), excludeJobsWithZeroEmployees: props.excludeJobsWithZeroEmployees },
      clear: true
    });
  }

  function handleEmployeeFilterToggle(filterEnabled) {
    props.actions.toggleEmployeeFilter()
    props.actions.search({
      params: { ...getCMSQueryParams(jobFacetName), ...lastQueryParams, excludeJobsWithZeroEmployees: filterEnabled },
    });
  }

  // Pagination logic for org job list
  function pageOrgJobs(limit, offset) {
    props.actions.pageList(limit, offset);
  }

  // clicking x to clear search text
  function searchOnClick() {
    if (scrollRef?.current?.scrollTop) {
      scrollRef.current.scrollTop = 0; // reset pagination scroll
    }
    props.actions.setSearchQuery('');
  }

  // when pressing enter to execute a search
  function searchOnKeyDown(event) {
    if (event.key === 'Enter') {
      if (scrollRef?.current?.scrollTop) {
        scrollRef.current.scrollTop = 0; // reset pagination scroll
      }
      props.actions.setSearchQuery(event.target.value);
    }
  }

  function showMarketplaceMatchesSlideout() {
    if (!selectedJobs.length) return;
    props.actions.showSlideout(MP_SLIDEOUTS.MARKETPLACE_MATCHES_NEW, { selectedJobIds: selectedJobs.map(job => job.jobID) });
  }

  // clicking the search icon to execute a search
  function searchOnSearch(value) {
    if (scrollRef?.current?.scrollTop) {
      scrollRef.current.scrollTop = 0; // reset pagination scroll
    }
    props.actions.setSearchQuery(value);
  }

  async function showCompSummary() {
    const numSelectedJobs = selectedJobs.length;
    if (numSelectedJobs === 0 || numSelectedJobs > 50) {
      return;
    }
    props.actions.getJobsByID(selectedJobs.map(job => job.jobID));
    props.actions.setSelectedBenchmarkYear(benchmarkYear);
    const orgJob = {
      id: selectedJobs[0]?.jobID
    };

    const selectedEmployeesViewPk = await props.actions.getEmployeeSelectedView();
    const selectedJobDetailsViewPk = await props.actions.getJobDetailsSelectedView(props.masterAccountId);

    props.history.push(`${props.history.location.pathname}/jobs/${orgJob.id}/compensation-summary-report`, {
      modal: true,
      modalType: 'COMPENSATION_SUMMARY_REPORT',
      modalProps: { className: 'comp-sum__modal', orgJob, selectedEmployeesViewPk, selectedJobDetailsViewPk },
      component: 'COMPENSATION_SUMMARY_REPORT',
      previousPath: props.history.location.pathname
    });
  }

  // Checks for empty facet or org job results
  const showPlaceHolder = props.grid?.data?.length === 0 && loaded && !loading;

  // Show if no orgs jobs are returned
  const noResultsPlaceholder = (
    <div className="landing-page__no-results-container">
      <Placeholder
        containerClassName="landing-page__no-results-container"
        primaryText="No org jobs found for current search critiera"
        secondaryText="Please select a different set of filters"
      />
    </div>
  );

  function setSelectedLandingPageView(view) {
    const selectedView = props.landingPageViews?.views?.find(l => l.view_name?.trim() === view?.trim());

    if (selectedView) {
      props.actions.setLandingPageSelectedView(selectedView.view_pk, props.masterAccountId);
    }
  }

  function getSelectedViewName(viewPk) {
    const selectedView = props.landingPageViews?.views?.find(v => v.view_pk === viewPk);

    return props.grid?.metadata?.viewName || selectedView?.view_name;
  }

  function toggleSearchView() {
    const newSearchMode = !useFacetSearch;
    props.gridActions.clearSort();
    localStorage.setItem(facetId + '_search_mode', newSearchMode);
    props.gridActions.setSearchMode(newSearchMode ? 'facet' : 'advanced');
    props.gridActions.clearResultsOnly();
    props.history.push();
    props.actions.resetLandingPageReducer().then(newSearchMode && handleReset);
    props.actions.clearEmployeeFilter()
    props.gridActions.clearAdvancedFilters();
    setUseFacetSearch(newSearchMode);
  }

  function getTextTerms(term) {
    return term.dataType === 'text';
  }

  function showConfigureViewSlideout() {
    props.actions.showSlideout(MP_SLIDEOUTS.CONFIGURE_VIEW, {
      onRefresh: refreshViews,
      categoryCode: CATEGORY_CODE.JOB_SEARCH,
      page: PAGE.JOB_SEARCH,
      pageCode: PAGE_CODE.JOB_SEARCH,
      pageName: 'Job Views',
      onRefreshData: () => props.actions.searchInputSearch(query),
      includeAggregates: true
    });
  }

  function getViewDropdown() {
    return (
      <div className="landing-page__dropdown-group">
        <label>View</label>
        <Dropdown
          className={classnames('landing-page__dropdown', { restricted: props.restrictedUser })}
          text={getSelectedViewName(props.landingPageSelectedView?.selectedViewPk)}
          customRenderComponent={DropdownViews}
          customRenderProps={{
            views: props.landingPageViews?.views,
            selectedViewPk: props.landingPageSelectedView?.selectedViewPk,
            onEdit: showConfigureViewSlideout,
            onSelectView: setSelectedLandingPageView,
            userRights: props.userRights,
            groupAdminCanConfigureViews: props.groupAdminCanConfigureViews,
            regularUserCanConfigureViews: props.regularUserCanConfigureViews
          }}
        />
      </div>
    );
  }

  const headerRender = (
    <FacetHeader
      toggleSearchView={toggleSearchView}
      useFacetSearch={useFacetSearch}
      showResetAllButton={showResetAllButton}
      handleReset={handleReset}
    />
  );

  const clearAllSelections = () => {
    dispatch(landingPageActions.clearAllSelections());
  };

  const generateCSRButtonClassNames = classnames('ps-button ps-button--link', {
    'muted-selection': selectedJobs.length === 0 || selectedJobs.length > 50
  });
  const generateMPMButtonClassNames = classnames('ps-button ps-button--link', {
    'muted-selection': selectedJobs.length === 0
  });
  const clearSelectionsButtonClassNames = classnames('ps-button ps-button--link', {
    'muted-selection': selectedJobs.length === 0
  });

  return (
    <div className="landing-page">
      {isLoading && useFacetSearch ? (
        <FacetPanelLoading
          className={'landing-page__facet-panel org__job-facets'}
          numberOfFacets={5}
          numberOfSubItems={5}
          includeHeader={true}
        />
      ) : (
        <FacetPanel
          className="landing-page__facet-panel org__job-facets"
          id="landing-page__facet-panel"
          facetItemClick={props.actions.facetItemClick}
          facetReset={props.actions.facetReset}
          lastFacetFilteredID={lastFacetFilteredID}
          facetSelection={facetSelection}
          facets={facets}
          hiddenFacets={getHiddenFacets('marketPricingLandingPage', props.useJobCodeKey)}
          isDisabled={false}
          isLoaded={loaded}
          limit={5}
          resetSearch={handleReset}
          showFacetSectionSearch
          showResetButton={showResetAllButton || false}
          showZeros={showZeros}
          title="Market Pricing"
          firstPageLoad={!firstPageLoaded}
          canCollapse={true}
          collapseCallback={setFacetCollapse}
          collapseTooltip={facetCollapse ? 'Close company job filters' : 'Open company job filters'}
          renderChildren={!useFacetSearch}
          headerRender={headerRender}
          isUncheckedInitially={true}
          lockOtherFacets={loading}
          scrollHeightCutoff={28}
          showEmployeeFilter
          employeeFilterValue={props.excludeJobsWithZeroEmployees}
          handleEmployeeFilterToggle={handleEmployeeFilterToggle}
        >
          <AdvancedSearch
            selectedViewPk={props.landingPageSelectedView?.selectedViewPk}
            availableFields={props.grid.terms?.filter(getTextTerms)}
            clearAdvancedFilters={props.gridActions.clearAdvancedFilters}
            setAdvancedFilters={props.gridActions.setAdvancedFilters}
            search={props.actions.searchInputSearch}
            currentAdvancedFilters={props.grid.addedFilters}
            clearGrid={props.gridActions.clearResultsOnly}
            defaultField="org_job_title"
            localFiltersKey={advancedSearchFilterKey}
            shouldInitialLoad={true}
          />
        </FacetPanel>
      )}

      <div className="landing-page__content">
        <div className="landing-page__search-container">
          {useFacetSearch && (
            <React.Fragment>
              <Search
                className={classnames('landing-page__search', { 'facet-offset': !facetCollapse })}
                onClick={searchOnClick}
                onKeyDown={searchOnKeyDown}
                onSearch={searchOnSearch}
                placeholder="Search by job title or job code"
                testId="landing-page__search"
                useInternalState
                ref={searchInputRef}
              />
              <SearchInfo className="landing-age__search-info" />
            </React.Fragment>
          )}
          <div className={classnames('landing-page__right-buttons', { ie: isIE() })}>
            {isLoading ? <span className="dropdown-loading" /> : getViewDropdown()}
            {!props.restrictedUser && (
              <Button
                className="pxl-btn landing-page__adhoc-button"
                component={Link}
                to="/market-analysis/jobs/adhoc/survey-library"
                size="xl"
                variant="outline"
              >
                Ad Hoc Pricing
              </Button>
            )}
            {!props.restrictedUser && !props.readOnlyUser && (
              <Tooltip
                label={
                  selectedJobs.length === 0 ?
                  'You must select at least one job' :
                  nonSlottedJobs.length < selectedJobs.length ?
                  'Please remove slotted jobs to continue' :
                  nonSlottedJobs.length > 50 ?
                  'You can select up to 50 jobs' :
                  'Price Jobs'
                }
                position="bottom"
              >
                <Button
                  className="bulk-pricing-btn bulk-pricing-btn__multi"
                  variant="filled"
                  size="xl"
                  component={Link}
                  to={{
                    pathname: '/market-analysis/multi-job-pricing',
                    state: { bulkPricing: true }
                  }}
                  data-disabled={disableBulkPricing}
                  onClick={disableBulkPricing && ((event) => event.preventDefault())}
                  color="marketpayBlue"
                >
                  {disableBulkPricing ? 'Price Jobs' : `Price ${nonSlottedJobs.length} ${nonSlottedJobs.length > 1 ? 'Jobs' : 'Job'}`}
                </Button>
              </Tooltip>
            )}
            {props.useJobCodeKey && props.usePropagateMatches && !props.restrictedUser && !props.readOnlyUser && (
              <Tooltip
                label={
                  selectedJobs.length === 0 ?
                  'You must select at least one job' :
                  nonSlottedJobs.length < selectedJobs.length ?
                  'Please remove slotted jobs to continue' :
                  nonSlottedJobs.length > 50 ?
                  'You can select up to 50 jobs' :
                  disablePropagateMatches ? 'All selected jobs must have a pricing status of Priced in order to propagate.' :
                  'Propagate'
                }
                position="bottom"
              >
                <Button
                  className="bulk-pricing-btn"
                  variant="filled"
                  size="xl"
                  component={Link}
                  to={{
                    pathname: '/market-analysis/propagate-matches',
                    state: { bulkPricing: true }
                  }}
                  data-disabled={disablePropagateMatches || disableBulkPricing}
                  onClick={(disablePropagateMatches || disableBulkPricing) && ((event) => event.preventDefault())}
                  color="marketpayBlue"
                >
                  Propagate
                </Button>
              </Tooltip>
            )}
          </div>
        </div>
        {isLoading ? (
          <LoadingTable className="landing-page__loading-table" numberOfRows={5} includeHeader={true} />
        ) : showPlaceHolder ? (
          noResultsPlaceholder
        ) : (
          <React.Fragment>
            <div className="selected-jobs__container">
              <span className={selectedJobs.length === 0 && 'none-selected'}>{`${selectedJobs.length} Selected`}</span>
              <span className="spacer">|</span>
              <Tooltip
                  label= {selectedJobs.length === 0 ? 'Please select a job to generate Compensation Summaries' : 'Generate Compensation Summary'}
                  position="bottom"
              >
                <button type="button" onClick={showCompSummary} className={generateCSRButtonClassNames} disabled={selectedJobs.length === 0 || selectedJobs.length > 50}>
                  {selectedJobs.length > 0 && (<i className="icon-upload icon" />)}
                  Generate Compensation Summary
                  {selectedJobs.length > 50 && ' (select up to 50)'}
                  {selectedJobs.length === 0 && (<i className="icon-info little-i" />)}
                </button>
              </Tooltip>
              {showMarketplaceMatchesSlideoutFeatureFlag && (
                <>
                  <span className="spacer">|</span>
                  <Tooltip
                    label= {selectedJobs.length === 0 ? 'Please select a job to open Marketplace Matches' : 'Open Marketplace Matches'}
                    position="bottom"
                  >
                    <button type="button" onClick={showMarketplaceMatchesSlideout} className={generateMPMButtonClassNames} disabled={selectedJobs.length === 0}>
                      {selectedJobs.length > 0 && (<i className="icon-users icon" />)}
                      Open Marketplace Matches
                      {selectedJobs.length === 0 && (<i className="icon-info little-i" />)}
                    </button>
                  </Tooltip>
                </>
              )}
              <span className="spacer">|</span>
              <button type="button" onClick={clearAllSelections} className={clearSelectionsButtonClassNames}>
                Clear All Selections
              </button>
            </div>
            <div className="landing-page__list-container" ref={scrollRef}>
              <LandingPageGrid
                history={props.history}
                masterAccountId={props.masterAccountId}
                search={props.actions.searchInputSearch}
                landingPageGrid={props.grid}
                landingPageGridActions={props.gridActions}
                jobMatchGrid={props.jobMatchGrid}
                jobMatchGridActions={props.jobMatchGridActions}
                readOnlyUser={props.readOnlyUser}
                restrictedUser={props.restrictedUser}
                offset={offset}
                useJobCodeKey={props.useJobCodeKey}
                jobCodeKeyText={jobCodeKeyText}
                loading={loading}
                searchQuery={query}
                useFacetSearch={useFacetSearch}
                numericRounding={numericRounding}
                percentRounding={percentRounding}
                benchmarkYear={benchmarkYear}
                setSelectedBenchmarkYear={props.actions.setSelectedBenchmarkYear}
                organizationCode={props.organizationCode}
              />
            </div>
            <div className="landing-page__pagination-container">
              <Pagination
                className="landing-page__pagination"
                offset={offset}
                onStepChange={pageOrgJobs}
                pageAction={pageOrgJobs}
                pageShowRange={3}
                scrollEl={scrollRef}
                scrollTopPageChange
                shouldShowInput
                step={limit}
                totalCount={props.grid?.metadata?.totalCount}
                allowChangingLimit={true}
                dropdownItems={[25, 50, 100]}
              />
            </div>
          </React.Fragment>
        )}
      </div>
    </div>
  );
}

const mapStateToProps = ({ aging, group, landingPage, session, slideout, marketPricing }) => ({
  ...landingPage.landingPageReducer,
  grid: landingPage.landingPageGridReducer,
  jobMatchGrid: landingPage.jobMatchGridReducer,
  benchmarkYear: aging.benchmarkYear,
  group,
  jobFacetSearch: landingPage.landingPageFacetReducer,
  replacementTokens: session.replacementTokens,
  useJobCodeKey: session.organization.useJobCodeKey,
  masterAccountId: session.organization.masterAccountId,
  fieldFormatting: {
    numericRounding: session.user.preferences.numericRounding,
    monetaryRounding: session.user.preferences.monetaryRounding,
    percentRounding: session.user.preferences.percentRounding
  },
  userRights: session.user.rights,
  groupAdminCanConfigureViews: session.groupAdminCanConfigureViews,
  regularUserCanConfigureViews: session.regularUserCanConfigureViews,
  slideoutOpen: slideout.open,
  refetchGridData: marketPricing.MarketPricingReducer.refetchData,
  organizationCode: session.organization.code
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(
    {
      ...landingPageActions,
      ...landingPageJobFacetSearchActions,
      showSlideout,
      hideSlideout,
      dataRefetched,
      setSelectedBenchmarkYear,
      getJobsByID,
      getEmployeeSelectedView,
      getJobDetailsSelectedView
    },
    dispatch
  ),
  gridActions: bindActionCreators(
    {
      ...landingPageGridActions
    },
    dispatch
  ),
  jobMatchGridActions: bindActionCreators(
    {
      ...jobMatchGridActions
    },
    dispatch
  )
});

export default connect(mapStateToProps, mapDispatchToProps)(LandingPage);
